import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';

// --- Constants ---

export const INQUIRY_COST_COINS = 30;

// --- Types ---

export interface PreferredTimeSlot {
  date: string;
  timeSlot: string;
}

export interface InquiryMessage {
  id: string;
  senderUserId: string | null;
  senderType: 'STUDENT' | 'TEACHER' | 'SYSTEM';
  senderName: string | null;
  content: string;
  createdAt: string;
  seenAt: string | null;
}

export type ThreadStatus =
  | 'SENT'
  | 'TEACHER_REPLIED'
  | 'STUDENT_REPLIED'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'ARCHIVED'
  | 'CLOSED';

export const OPEN_THREAD_STATUSES: ReadonlySet<ThreadStatus> = new Set([
  'SENT', 'TEACHER_REPLIED', 'STUDENT_REPLIED', 'ACTIVE', 'INACTIVE',
]);

export interface InquiryThread {
  id: string;
  studentUserId: string;
  studentName: string;
  teacherUserId: string;
  teacherName: string;
  teacherHandle: string | null;
  teacherDisplayName: string | null;
  teacherLocation: string | null;
  subject: string;
  status: ThreadStatus;
  shareContactInfo: boolean;
  sharedEmail: string | null;
  preferredTimeSlots: PreferredTimeSlot[] | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessage: InquiryMessage | null;
  unreadCount: number;
  gradeBand: string | null;
  curriculum: string | null;
  goalType: string | null;
  modePreference: string | null;
  startTimeline: string | null;
  /**
   * AI-derived lead-quality band. Nullable and OPTIONAL: absent entirely when
   * the backend feature is off (fail-dormant — render nothing), explicitly
   * `null` when present but not yet scored (render "Not yet rated").
   */
  leadScoreBand?: "STRONG" | "PROMISING" | "LOW_SIGNAL" | null;
  /** 2-3 short reasons backing `leadScoreBand`. Absent/null when unscored. */
  leadScoreReasons?: string[] | null;
  /** Money-back guarantee state for this thread's coin charge. Absent/null hides the guarantee note. */
  guaranteeState?: "HELD" | "KEPT" | "REFUNDED" | "KEPT_OVER_CAP" | "UNREFUNDABLE" | null;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface CreateInquiryRequest {
  teacherUserId: string;
  subject: string;
  message: string;
  shareContactInfo?: boolean;
  preferredTimeSlots?: PreferredTimeSlot[];
  gradeBand?: string;
  curriculum?: string;
  goalType?: string;
  modePreference?: string;
  startTimeline?: string;
  /** The offering the student was viewing when starting this inquiry (ADR-088 AC10). */
  offeringId?: string;
}

export interface EmailPreferences {
  inquiryEmails: boolean;
  reminderEmails: boolean;
}

// --- Student Inquiry API ---

export async function createInquiry(
  accessToken: string,
  data: CreateInquiryRequest
): Promise<InquiryThread> {
  const response = await apiFetch('/api/v1/inquiries', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
  if (response.status === 401) handleUnauthorized();
  if (response.status === 409) throw new Error('THREAD_EXISTS');
  if (response.status === 402) throw new Error('INSUFFICIENT_COINS');
  if (response.status === 400) {
    const text = await response.text();
    throw new Error(text || 'Invalid request');
  }
  if (!response.ok) throw new Error(`Failed to create inquiry: ${response.status}`);
  return response.json();
}

export async function fetchMyInquiries(
  accessToken: string,
  page = 0,
  size = 20,
  status?: string
): Promise<PageResponse<InquiryThread>> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);

  const response = await apiFetchWithRetry(`/api/v1/inquiries?${params}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch inquiries: ${response.status}`);
  return response.json();
}

export async function fetchThread(
  accessToken: string,
  threadId: string
): Promise<InquiryThread> {
  const response = await apiFetchWithRetry(`/api/v1/inquiries/${threadId}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch thread: ${response.status}`);
  return response.json();
}

export async function fetchMessages(
  accessToken: string,
  threadId: string,
  page = 0,
  size = 50
): Promise<PageResponse<InquiryMessage>> {
  const response = await apiFetchWithRetry(
    `/api/v1/inquiries/${threadId}/messages?page=${page}&size=${size}`,
    { headers: { 'Authorization': `Bearer ${accessToken}` } }
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch messages: ${response.status}`);
  return response.json();
}

export async function sendMessage(
  accessToken: string,
  threadId: string,
  content: string
): Promise<InquiryMessage> {
  const response = await apiFetch(`/api/v1/inquiries/${threadId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ content }),
  });
  if (response.status === 401) handleUnauthorized();
  if (response.status === 403) throw new Error('THREAD_NOT_OPEN');
  if (response.status === 429) throw new Error('RATE_LIMITED');
  if (!response.ok) throw new Error(`Failed to send message: ${response.status}`);
  return response.json();
}

export async function markThreadAsRead(
  accessToken: string,
  threadId: string
): Promise<void> {
  const response = await apiFetch(`/api/v1/inquiries/${threadId}/read`, {
    method: 'PATCH',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
}

export async function closeThread(
  accessToken: string,
  threadId: string
): Promise<InquiryThread> {
  const response = await apiFetch(`/api/v1/inquiries/${threadId}/close`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (response.status === 403) throw new Error('NOT_PARTICIPANT');
  if (!response.ok) throw new Error(`Failed to close thread: ${response.status}`);
  return response.json();
}

// --- Teacher Inquiry API ---

export async function fetchTeacherInquiries(
  accessToken: string,
  page = 0,
  size = 20,
  status?: string
): Promise<PageResponse<InquiryThread>> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);

  const response = await apiFetchWithRetry(`/api/v1/teacher/inquiries?${params}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch teacher inquiries: ${response.status}`);
  return response.json();
}

export async function fetchTeacherThread(
  accessToken: string,
  threadId: string
): Promise<InquiryThread> {
  const response = await apiFetchWithRetry(`/api/v1/teacher/inquiries/${threadId}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch teacher thread: ${response.status}`);
  return response.json();
}

export async function sendTeacherMessage(
  accessToken: string,
  threadId: string,
  content: string
): Promise<InquiryMessage> {
  const response = await apiFetch(`/api/v1/teacher/inquiries/${threadId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ content }),
  });
  if (response.status === 401) handleUnauthorized();
  if (response.status === 403) throw new Error('THREAD_NOT_OPEN');
  if (response.status === 429) throw new Error('RATE_LIMITED');
  if (response.status === 402) throw new Error('INSUFFICIENT_COINS');
  if (!response.ok) throw new Error(`Failed to send message: ${response.status}`);
  return response.json();
}

export async function markTeacherThreadAsRead(
  accessToken: string,
  threadId: string
): Promise<void> {
  const response = await apiFetch(`/api/v1/teacher/inquiries/${threadId}/read`, {
    method: 'PATCH',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
}

export async function closeTeacherThread(
  accessToken: string,
  threadId: string
): Promise<InquiryThread> {
  const response = await apiFetch(`/api/v1/teacher/inquiries/${threadId}/close`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (response.status === 403) throw new Error('NOT_PARTICIPANT');
  if (!response.ok) throw new Error(`Failed to close thread: ${response.status}`);
  return response.json();
}

// --- Email Preferences API ---

export async function fetchEmailPreferences(
  accessToken: string
): Promise<EmailPreferences> {
  const response = await apiFetchWithRetry('/api/v1/settings/email-preferences', {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch email preferences: ${response.status}`);
  return response.json();
}

export async function updateEmailPreferences(
  accessToken: string,
  prefs: EmailPreferences
): Promise<EmailPreferences> {
  const response = await apiFetch('/api/v1/settings/email-preferences', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(prefs),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to update email preferences: ${response.status}`);
  return response.json();
}
